import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  ButtonBase,
  Stack,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import { Fade } from "react-awesome-reveal";

const rotatingTexts = [
  "Más reservas sin complicarte",
  "Ahorro de tiempo todos los días",
  "Tecnología simple que funciona",
];

const cards = [
  {
    title: "Automatización",
    description:
      "Dejá que el sistema avise, sincronice y cobre por vos. Menos tareas, menos errores.",
    icon: <AutoModeIcon sx={{ fontSize: 48, color: "#903AF2" }} />,
    pills: [
      "Mensajes automáticos",
      "Recordatorios smart",
      "Menos tareas manuales",
    ],
  },
  {
    title: "Más ventas directas",
    description:
      "Motor de reservas optimizado para que te reserven desde tu web, sin comisiones.",
    icon: <TrendingUpIcon sx={{ fontSize: 48, color: "#903AF2" }} />,
    pills: [
      "Sin comisiones abusivas",
      "Reserva en 2 pasos",
      "Mejor conversión web",
    ],
  },
  {
    title: "Gestión simple",
    description:
      "Todo en un panel claro: calendario, pagos y reportes en tiempo real.",
    icon: <DashboardCustomizeIcon sx={{ fontSize: 48, color: "#903AF2" }} />,
    pills: [
      "Calendario unificado",
      "Pagos y reportes",
      "Todo en un solo lugar",
    ],
  },
];

export default function BenefitsShowcase() {
  const [index, setIndex] = useState(0);
  const prefersReduced = useMediaQuery("(prefers-reduced-motion: reduce)");

  // mismo timing que antes; la animación ahora incluye “salida”
  useEffect(() => {
    if (prefersReduced) return;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % rotatingTexts.length);
    }, 2600);
    return () => clearInterval(id);
  }, [prefersReduced]);

  const headline = useMemo(() => rotatingTexts[index], [index]);

  return (
    <Box
      sx={{
        position: "relative",
        textAlign: "center",
        py: { xs: 4, md: 6 },

        background:
          "linear-gradient(180deg, #903AF2 25%, #b88af6 70%, #ffffff 100%)",

        // ✅ keyframes para entrada + salida (slide izq->der con fade y exit)
        "@keyframes ubikaHeadlineSlide": {
          "0%": { opacity: 0, transform: "translateX(-18px)" },
          "12%": { opacity: 1, transform: "translateX(0px)" },
          "78%": { opacity: 1, transform: "translateX(0px)" },
          "100%": { opacity: 0, transform: "translateX(18px)" },
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Titular rotativo + claim */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Box
            sx={{
              minHeight: { xs: 56, md: 68 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {prefersReduced ? (
              <Typography
                sx={{
                  color: "#fff", // ✅ blanco por fondo violeta
                  fontWeight: 900,
                  fontFamily: "'Red Hat Display', sans-serif",
                  letterSpacing: 0.2,
                  fontSize: { xs: "1.75rem", md: "2.15rem" },
                  textShadow: "0 10px 26px rgba(0,0,0,0.22)",
                }}
              >
                {headline}
              </Typography>
            ) : (
              <Typography
                key={headline} // ✅ fuerza restart de keyframes (incluye salida/entrada)
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  fontFamily: "'Red Hat Display', sans-serif",
                  letterSpacing: 0.2,
                  fontSize: { xs: "1.75rem", md: "2.15rem" },
                  textShadow: "0 10px 26px rgba(0,0,0,0.22)",
                  animation: "ubikaHeadlineSlide 2.6s ease-in-out both",
                  willChange: "transform, opacity",
                }}
              >
                {headline}
              </Typography>
            )}
          </Box>

          <Fade triggerOnce delay={120}>
            <Typography
              sx={{
                mt: 0.75,
                color: "rgba(255,255,255,0.92)", // ✅ blanco suave
                fontWeight: 800,
                fontFamily: "'Red Hat Display', sans-serif",
                fontSize: { xs: "1.2rem", md: "1.55rem" },
                textShadow: "0 8px 20px rgba(0,0,0,0.18)",
              }}
            >
              solo con Ubika
            </Typography>
          </Fade>

          {/* ✅ divider nuevo con colores Ubika (EE964B + EAF7CF + toque amarillo) */}
          <Box
            sx={{
              mx: "auto",
              mt: 0.9,
              width: { xs: 120, md: 160 },
              height: 5,
              borderRadius: 999,
              background:
                "linear-gradient(90deg, #EE964B 0%, #FFE735 35%, #EAF7CF 100%)",
              boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
              transform: "translateZ(0)",
            }}
          />
        </Box>

        {/* Cards */}
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          justifyContent="center"
          alignItems="stretch"
        >
          {cards.map((card, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx} sx={{ display: "flex" }}>
              <Fade triggerOnce fraction={0.2} delay={idx * 80}>
                <ButtonBase
                  focusRipple
                  sx={{
                    width: "100%",
                    textAlign: "left",
                    borderRadius: 3,
                    outline: "none",
                    display: "flex",
                    "&:focus-visible": {
                      boxShadow: "0 0 0 3px #FFE735",
                    },
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2.5, md: 3 },
                      width: "100%",
                      height: "100%",
                      minHeight: { xs: 260, md: 300 },
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.1,
                      alignItems: "flex-start",
                      borderRadius: 3,
                      backgroundColor: "#ffffff",
                      border: "1px solid rgba(0,0,0,0.06)",
                      boxShadow: "0 0 22px rgba(255,255,255,0.8)",
                      transition: "transform .25s ease, box-shadow .25s ease",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow:
                          "0 10px 32px rgba(0,0,0,0.12), 0 0 24px rgba(255,255,255,0.85)",
                      },
                    }}
                  >
                    <Box sx={{ mb: 0.25 }}>{card.icon}</Box>

                    <Typography
                      sx={{
                        color: "#1d1d1f",
                        fontWeight: 800,
                        fontFamily: "'Red Hat Display', sans-serif",
                        fontSize: { xs: "1.08rem", md: "1.2rem" },
                        lineHeight: 1.15,
                      }}
                    >
                      {card.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(0,0,0,0.75)",
                        fontFamily: "'Merriweather Sans', sans-serif",
                        fontSize: { xs: "0.94rem", md: "1.0rem" },
                      }}
                    >
                      {card.description}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      mt="auto"
                      pt={1.5}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {card.pills.map((t) => (
                        <Pill key={t}>{t}</Pill>
                      ))}
                    </Stack>
                  </Paper>
                </ButtonBase>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* CTA inferior */}
        <Fade triggerOnce delay={220}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: { xs: 3, md: 4 } }}
          >
            <Button
              variant="contained"
              onClick={() =>
                (window.location.href = "https://wa.me/542604206967")
              }
              sx={{
                px: 3.2,
                py: 1.15,
                borderRadius: 2.5,
                fontWeight: 900,
                fontFamily: "'Red Hat Display', sans-serif",
                fontSize: { xs: "1rem", md: "1.05rem" },
                textTransform: "none",
                backgroundColor: "#EE964B",
                boxShadow: "0 12px 22px rgba(0,0,0,0.16)",
                "&:hover": { backgroundColor: "#e3883b" },
                "&:focus-visible": {
                  outline: "3px solid #FFE735",
                  outlineOffset: "2px",
                },
              }}
            >
              Quiero más reservas
            </Button>
          </Stack>
        </Fade>
      </Container>
    </Box>
  );
}

/* --- Pequeños componentes de estilo reutilizables --- */
function Pill({ children }) {
  return (
    <Box
      sx={{
        px: 1.2,
        py: 0.35,
        borderRadius: 999,
        fontSize: { xs: "0.78rem", md: "0.82rem" },
        fontWeight: 700,
        color: "#903AF2",
        backgroundColor: "rgba(144,58,242,0.08)",
        border: "1px solid rgba(144,58,242,0.22)",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}
